import ArticleUpdaterService from "@components/Article/ArticleUpdater/ArticleUpdaterService";
import InputFile from "@components/Atoms/InputFile";
import ArticlePropsService from "@core-ui/ContextServices/ArticleProps";
import ButtonStateService from "@core-ui/ContextServices/ButtonStateService/ButtonStateService";
import { cn } from "@core-ui/utils/cn";
import styled from "@emotion/styled";
import t from "@ext/localization/locale/translate";
import ResourceService from "@ext/markdown/elements/copyArticles/resourceService";
import { Editor } from "@tiptap/core";
import { Icon } from "@ui-kit/Icon";
import { ToolbarDropdownMenuItem } from "@ui-kit/Toolbar";
import { ChangeEvent, useCallback } from "react";
import createImages from "../logic/createImages";

interface ImageMenuButtonProps {
	editor: Editor;
	className?: string;
	fileName?: string;
	onSave?: () => void;
	onStart?: () => void;
}

const ImageMenuButton = ({ editor, className, fileName, onSave, onStart }: ImageMenuButtonProps) => {
	const articleProps = ArticlePropsService.value;
	const resourceService = ResourceService.value;

	const { disabled, isActive } = ButtonStateService.useCurrentAction({ action: "image" });

	const onAbort = useCallback(() => {
		onSave?.();
	}, [onSave]);

	const onChange = useCallback(
		async (event: ChangeEvent<HTMLInputElement>) => {
			await createImages(
				[...event.currentTarget.files],
				editor.view,
				fileName || articleProps?.fileName,
				resourceService,
			);
			event.target.value = "";
			onSave?.();
		},
		[editor.view, fileName, articleProps?.fileName, resourceService, onSave],
	);

	if (disabled) {
		return (
			<ToolbarDropdownMenuItem
				active={isActive}
				dataQa={`qa-edit-menu-image`}
				disabled={disabled}
				onSelect={() => ArticleUpdaterService.stopLoadingAfterFocus()}
			>
				<div className="flex flex-row items-center gap-2 w-full">
					<Icon icon="image" />
					{t("image")}
				</div>
			</ToolbarDropdownMenuItem>
		);
	}

	return (
		<ToolbarDropdownMenuItem
			active={isActive}
			className={cn(className, "flex flex-row items-center gap-2 whitespace-nowrap")}
			dataQa={`qa-edit-menu-image`}
			disabled={disabled}
			onSelect={(e) => {
				e.preventDefault();
				onStart?.();
				ArticleUpdaterService.stopLoadingAfterFocus();
			}}
		>
			<InputFile
				className="flex flex-row items-center w-full cursor-pointer"
				onAbort={onAbort}
				onChange={onChange}
			>
				<div className="flex flex-row items-center gap-2 w-full">
					<Icon icon="image" />
					{t("image")}
				</div>
			</InputFile>
		</ToolbarDropdownMenuItem>
	);
};

export default styled(ImageMenuButton)`
	position: relative;

	input[type="file"] {
		position: absolute;
		z-index: var(--z-index-background);
		opacity: 0;
		display: block;
		width: 0;
		height: 0;
	}
`;
